import { type FC, useCallback } from "react";

import { AutoSizer, Button, SearchableCard, VirtualTable } from "@snapchat/snap-design-system";

import { type MyLensesLens } from "../../gql-generated/types";
import { useLensesOfSelectedContainer } from "../../lenses/hooks";
import { useTableFilterAndSort } from "../../shared/hooks";
import { PaginationAtomsKeys } from "../../shared/state";

import { Disclaimer } from "./EmptyHostedLens.styles";
import { EmptyTableMessage } from "./EmptyTableMessage";
import { backButton, containerCSS, fullHeightCardCSS, rowClassname } from "./LensList.styles";
import { getColumns } from "./LensListColumns";
import { HostedLensDisclaimer } from "./messages.generated";

interface LensListProps {
    handleView: (view: "folder" | "lens") => void;
    setSelectedLens: (lensId: string) => void;
    selectedLensId: string;
}

export const LensList: FC<LensListProps> = ({ handleView, setSelectedLens, selectedLensId }) => {
    const { lenses, lensSearchText, setLensSearchText, loadMore, hasMore, isFirstFetching, isReFetching } =
        useLensesOfSelectedContainer(PaginationAtomsKeys.LensesList);
    const getSearchKey = useCallback((lens: MyLensesLens) => `${lens.name} ${lens.id}`, []);
    const { data, onColumnSort } = useTableFilterAndSort({
        tableData: lenses,
        initialColumn: "name",
        searchText: lensSearchText,
        getSearchKey,
    });

    const handleClick = useCallback(
        (selectedId: string) => {
            setSelectedLens(selectedId);
        },
        [setSelectedLens],
    );

    return (
        <div className={containerCSS}>
            <Button
                className={backButton}
                type={"link"}
                onClick={() => {
                    handleView("folder");
                    setSelectedLens("");
                }}
            >
                &lt; Back to folders
            </Button>
            <SearchableCard
                className={fullHeightCardCSS}
                searchPlaceholder="Search for a lens."
                padBody={false}
                onSearch={setLensSearchText}
            >
                <AutoSizer>
                    {({ height, width }) => (
                        <VirtualTable
                            data={[...data]}
                            columns={getColumns(selectedLensId, handleClick)}
                            width={width}
                            height={height}
                            isLoading={isFirstFetching || isReFetching}
                            hasMore={hasMore}
                            rowHeight={60}
                            emptyMessage={
                                <EmptyTableMessage
                                    isLoading={isFirstFetching || isReFetching}
                                    message="No lenses found"
                                />
                            }
                            loadMore={loadMore}
                            onRowClick={(_, rowIndex) => {
                                if (data[rowIndex]?.unlockableId === 0) return;
                                handleClick(data[rowIndex]?.id ?? "");
                            }}
                            onColumnSort={onColumnSort}
                            onRowMouseOver={(event: React.MouseEvent<HTMLDivElement>, rowIndex) => {
                                if (data[rowIndex]?.unlockableId === 0) {
                                    event.currentTarget.style.cursor = "not-allowed";
                                }
                            }}
                            rowClassName={rowClassname}
                        />
                    )}
                </AutoSizer>
            </SearchableCard>
            <Disclaimer>
                <HostedLensDisclaimer />
            </Disclaimer>
        </div>
    );
};
